'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_STEP_PX = 200;
// Aire que queda al costado del elemento al traerlo a la vista.
const SCROLL_PADDING_PX = 8;

/**
 * Franja horizontal scrolleable con flechas en vez de barra de scroll (que come
 * alto) y sin envolver a varias filas (que hace crecer el componente a lo alto).
 *
 * `contentKey` fuerza el recálculo cuando cambia el CONTENIDO: el ResizeObserver
 * solo ve el contenedor, y cuando este tiene un ancho acotado agregar o renombrar
 * items cambia el `scrollWidth` sin cambiar su tamaño.
 */
export const useHorizontalScroller = <T extends HTMLElement = HTMLDivElement>(
  contentKey?: unknown,
) => {
  const ref = useRef<T>(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  const syncScrollState = useCallback(() => {
    const container = ref.current;

    if (!container) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    setCanScroll({
      left: container.scrollLeft > 1,
      // 1px de tolerancia: los anchos sub-pixel nunca llegan al máximo exacto.
      right: container.scrollLeft < maxScrollLeft - 1,
    });
  }, []);

  useEffect(() => {
    const container = ref.current;

    if (!container) return;

    syncScrollState();

    const observer = new ResizeObserver(syncScrollState);
    observer.observe(container);

    return () => observer.disconnect();
  }, [syncScrollState, contentKey]);

  const scrollByStep = useCallback((direction: 1 | -1) => {
    ref.current?.scrollBy({
      left: direction * SCROLL_STEP_PX,
      behavior: 'smooth',
    });
  }, []);

  const scrollLeft = useCallback(() => scrollByStep(-1), [scrollByStep]);
  const scrollRight = useCallback(() => scrollByStep(1), [scrollByStep]);

  /**
   * Trae `element` a la vista moviendo SOLO este contenedor. `scrollIntoView`
   * nativo escala hacia los ancestros y termina moviendo la página entera.
   */
  const scrollElementIntoView = useCallback(
    (element: HTMLElement | null, behavior: ScrollBehavior = 'smooth') => {
      const container = ref.current;

      if (!container || !element) return;

      const containerBounds = container.getBoundingClientRect();
      const elementBounds = element.getBoundingClientRect();

      const overflowLeft = containerBounds.left - elementBounds.left;
      const overflowRight = elementBounds.right - containerBounds.right;

      if (overflowLeft > 0) {
        container.scrollBy({
          left: -(overflowLeft + SCROLL_PADDING_PX),
          behavior,
        });
        return;
      }

      if (overflowRight > 0) {
        container.scrollBy({
          left: overflowRight + SCROLL_PADDING_PX,
          behavior,
        });
      }
    },
    [],
  );

  return {
    ref,
    canScrollLeft: canScroll.left,
    canScrollRight: canScroll.right,
    hasOverflow: canScroll.left || canScroll.right,
    syncScrollState,
    scrollLeft,
    scrollRight,
    scrollElementIntoView,
  };
};
